import { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import { BrowserRouter, Link, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  BadgeEuro,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  ExternalLink,
  Gift,
  LogIn,
  Menu,
  Minus,
  Package,
  Plus,
  RefreshCw,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Ticket,
  Trash2,
  X,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import {
  AdminDashboardPage,
  BlacklistedPage,
  CreatorDashboardPage,
  FiveMReturnPage,
  trackEvent,
} from "@/AdminSystem";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BASKET_KEY = "berlin-roleplay-basket-ident";
const PENDING_ADD_KEY = "berlin-roleplay-pending-add";

const navigation = [
  { label: "Startseite", href: "/", testid: "nav-home-link" },
  { label: "Coins", href: "/coins", testid: "nav-coins-link" },
  { label: "Imports", href: "/imports", testid: "nav-imports-link" },
  { label: "Pakete", href: "/pakete", testid: "nav-pakete-link" },
];

const bucketMeta = {
  coins: {
    label: "Coins",
    eyebrow: "Ingame-Währung",
    description: "Schnell verfügbare Coins für Extras und Upgrades auf Berlin Roleplay.",
    icon: BadgeEuro,
  },
  imports: {
    label: "Imports",
    eyebrow: "Fahrzeuge & Specials",
    description: "Ausgewählte Import-Angebote für ein exklusives Spielerlebnis.",
    icon: Sparkles,
  },
  pakete: {
    label: "Pakete",
    eyebrow: "Bundles & Vorteile",
    description: "Rundum-Pakete, Bundles und weitere digitale Angebote.",
    icon: Package,
  },
};

function formatMoney(price, fallbackCurrency = "EUR") {
  const amount = typeof price === "number" ? price : Number(price?.amount ?? 0);
  const currency = price?.currency || fallbackCurrency;
  return new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(amount || 0);
}

function plainText(html) {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

function normalizeApiError(error) {
  return error?.response?.data?.error || error?.message || "Unbekannter Fehler";
}

function formatGiftcardResult(data) {
  if (!data || typeof data !== "object") return "Gutschein/Giftcard wurde gefunden.";

  const currency = data.currency || "EUR";
  const balance = data.balance?.remaining ?? data.balance?.starting ?? data.remaining ?? data.value ?? data.amount;
  const remaining = data.remaining ?? data.balance?.remaining;
  const starting = data.starting ?? data.balance?.starting;
  const note = data.note || data.description || "";

  const parts = [];

  if (remaining !== undefined && remaining !== null) {
    parts.push(`Verfügbar: ${formatMoney(Number(remaining), currency)}`);
  } else if (balance !== undefined && balance !== null) {
    parts.push(`Wert: ${formatMoney(Number(balance), currency)}`);
  }

  if (starting !== undefined && starting !== null && String(starting) !== String(remaining)) {
    parts.push(`Startwert: ${formatMoney(Number(starting), currency)}`);
  }

  if (data.expires_at) {
    parts.push(`Gültig bis: ${new Date(data.expires_at).toLocaleDateString("de-DE")}`);
  }

  if (note) {
    parts.push(`Hinweis: ${note}`);
  }

  return parts.length ? parts.join(" · ") : "Gutschein/Giftcard ist gültig.";
}

function useShop() {
  const [products, setProducts] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/tebex/products`);
      if (!response.data.success) throw new Error(response.data.error || "Katalog konnte nicht geladen werden.");
      setProducts(response.data.data || []);
      setBuckets(response.data.buckets || []);
    } catch (err) {
      setError(normalizeApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  return { products, buckets, loading, error, reload: loadCatalog };
}

function useBasket() {
  const [basketIdent, setBasketIdent] = useState(() => localStorage.getItem(BASKET_KEY) || "");
  const [basket, setBasket] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [authPrompt, setAuthPrompt] = useState(null);

  const saveBasket = useCallback((ident, data) => {
    if (ident) {
      localStorage.setItem(BASKET_KEY, ident);
      setBasketIdent(ident);
    }
    if (data) setBasket(data);
  }, []);

  const refreshBasket = useCallback(
    async (ident = basketIdent, silent = false) => {
      if (!ident) return null;
      try {
        const response = await axios.get(`${API}/tebex/basket/${encodeURIComponent(ident)}`);
        if (response.data.success) {
          saveBasket(response.data.data.ident || ident, response.data.data);
          return response.data.data;
        }
        if (!silent) toast.error(response.data.error || "Warenkorb konnte nicht geladen werden.");
      } catch (err) {
        if (!silent) toast.error(normalizeApiError(err));
      }
      return null;
    },
    [basketIdent, saveBasket],
  );

  const createBasket = useCallback(async () => {
    const origin = window.location.origin;
    const response = await axios.post(`${API}/tebex/basket/create`, {
      complete_url: `${origin}/checkout/success`,
      cancel_url: `${origin}/warenkorb`,
    });
    if (!response.data.success) throw new Error(response.data.error || "Warenkorb konnte nicht erstellt werden.");
    saveBasket(response.data.data.ident, response.data.data);
    return response.data.data.ident;
  }, [saveBasket]);

  const ensureBasket = useCallback(async () => {
    if (basketIdent) return basketIdent;
    return createBasket();
  }, [basketIdent, createBasket]);

  const startAuthRedirect = useCallback((authUrl, pendingAdd = null) => {
    if (pendingAdd) localStorage.setItem(PENDING_ADD_KEY, JSON.stringify(pendingAdd));
    window.location.href = authUrl;
  }, []);

  const addToCart = useCallback(
    async (product, quantity = 1, options = {}) => {
      setBusy(true);
      let ident = basketIdent;
      try {
        ident = await ensureBasket();
        const returnUrl = `${window.location.origin}/auth/tebex-return?basket=${encodeURIComponent(ident)}&resumePackage=${encodeURIComponent(product.id)}&qty=${quantity}`;
        const response = await axios.post(`${API}/tebex/basket/${encodeURIComponent(ident)}/add`, {
          package_id: product.id,
          quantity,
          return_url: returnUrl,
        });
        if (response.data.success) {
          saveBasket(response.data.data.ident || ident, response.data.data);
          setCartOpen(true);
          toast.success(`${product.name} wurde in den Warenkorb gelegt.`);
          return { ok: true, basket: response.data.data };
        }
        if (response.data.code === "AUTH_REQUIRED" && response.data.auth_url) {
          const pendingAdd = { productId: product.id, quantity, productName: product.name, basketIdent: ident, ts: Date.now() };
          if (options.autoRedirect) {
            startAuthRedirect(response.data.auth_url, pendingAdd);
          } else {
            setAuthPrompt({ authUrl: response.data.auth_url, pendingAdd, productName: product.name });
          }
          return { ok: false, authRequired: true };
        }
        toast.error(response.data.error || "Produkt konnte nicht hinzugefügt werden.");
        return { ok: false };
      } catch (err) {
        if (err?.response?.data?.code === "AUTH_REQUIRED" && err.response.data.auth_url) {
          const pendingAdd = { productId: product.id, quantity, productName: product.name, basketIdent: ident, ts: Date.now() };
          if (options.autoRedirect) {
            startAuthRedirect(err.response.data.auth_url, pendingAdd);
          } else {
            setAuthPrompt({ authUrl: err.response.data.auth_url, pendingAdd, productName: product.name });
          }
          return { ok: false, authRequired: true };
        }
        toast.error(normalizeApiError(err));
        return { ok: false };
      } finally {
        setBusy(false);
      }
    },
    [basketIdent, ensureBasket, saveBasket, startAuthRedirect],
  );

  const removeItem = useCallback(
    async (productId) => {
      if (!basketIdent) return;
      setBusy(true);
      try {
        const response = await axios.post(`${API}/tebex/basket/${encodeURIComponent(basketIdent)}/remove`, { package_id: productId });
        if (response.data.success) {
          saveBasket(response.data.data.ident || basketIdent, response.data.data);
          toast.success("Produkt wurde entfernt.");
        } else {
          toast.error(response.data.error || "Produkt konnte nicht entfernt werden.");
        }
      } catch (err) {
        toast.error(normalizeApiError(err));
      } finally {
        setBusy(false);
      }
    },
    [basketIdent, saveBasket],
  );

  const updateQuantity = useCallback(
    async (productId, quantity) => {
      if (!basketIdent) return;
      setBusy(true);
      try {
        const response = await axios.post(`${API}/tebex/basket/${encodeURIComponent(basketIdent)}/update`, {
          package_id: productId,
          quantity,
          return_url: `${window.location.origin}/auth/tebex-return?basket=${encodeURIComponent(basketIdent)}&resumePackage=${encodeURIComponent(productId)}&qty=${quantity}`,
        });
        if (response.data.success) {
          saveBasket(response.data.data.ident || basketIdent, response.data.data);
        } else if (response.data.code === "AUTH_REQUIRED" && response.data.auth_url) {
          setAuthPrompt({
            authUrl: response.data.auth_url,
            pendingAdd: { productId, quantity, basketIdent, ts: Date.now() },
            productName: "dieses Produkt",
          });
        } else {
          toast.error(response.data.error || "Menge konnte nicht geändert werden.");
        }
      } catch (err) {
        toast.error(normalizeApiError(err));
      } finally {
        setBusy(false);
      }
    },
    [basketIdent, saveBasket],
  );

  const applyCode = useCallback(
    async (type, code) => {
      if (!code?.trim()) return false;
      setBusy(true);
      try {
        const ident = await ensureBasket();
        const endpoint = type === "coupon" ? "apply-coupon" : "apply-creator-code";

        const previousBasket = basket;
        const previousItems = previousBasket?.items || [];

        const response = await axios.post(`${API}/tebex/basket/${encodeURIComponent(ident)}/${endpoint}`, { code: code.trim() });

        if (response.data.success) {
          const returnedBasket = response.data.data || null;
          const returnedItems = returnedBasket?.items || [];

          if (returnedBasket?.ident && returnedItems.length > 0) {
            saveBasket(returnedBasket.ident || ident, returnedBasket);
          }

          let freshBasket = null;

          for (let attempt = 0; attempt < 4; attempt += 1) {
            if (attempt > 0) {
              await new Promise((resolve) => setTimeout(resolve, 450));
            }

            freshBasket = await refreshBasket(returnedBasket?.ident || ident, true);

            if (freshBasket?.items?.length || previousItems.length === 0) {
              break;
            }
          }

          if (freshBasket?.items?.length || previousItems.length === 0) {
            saveBasket(freshBasket?.ident || returnedBasket?.ident || ident, freshBasket || returnedBasket);
          } else if (previousBasket) {
            saveBasket(previousBasket.ident || ident, {
              ...previousBasket,
              creator_code: returnedBasket?.creator_code || previousBasket.creator_code || (type === "creator" ? code.trim() : previousBasket.creator_code),
              coupons: returnedBasket?.coupons || previousBasket.coupons || [],
              total_price: returnedBasket?.total_price ?? previousBasket.total_price,
              base_price: returnedBasket?.base_price ?? previousBasket.base_price,
              sales_tax: returnedBasket?.sales_tax ?? previousBasket.sales_tax,
            });
          }

          toast.success(type === "coupon" ? "Coupon wurde angewendet." : "Creator Code wurde angewendet.");
          return true;
        }

        toast.error(response.data.error || "Code konnte nicht angewendet werden.");
      } catch (err) {
        toast.error(normalizeApiError(err));
      } finally {
        setBusy(false);
      }
      return false;
    },
    [basket, ensureBasket, refreshBasket, saveBasket],
  );

  const removeCode = useCallback(
    async (type) => {
      if (!basketIdent) return;
      setBusy(true);
      try {
        const previousBasket = basket;
        const previousItems = previousBasket?.items || [];

        const endpoint = type === "coupon" ? "remove-coupon" : "remove-creator-code";
        const response = await axios.post(`${API}/tebex/basket/${encodeURIComponent(basketIdent)}/${endpoint}`);

        if (response.data.success) {
          const returnedBasket = response.data.data || null;
          const returnedItems = returnedBasket?.items || [];

          if (returnedBasket?.ident && returnedItems.length > 0) {
            saveBasket(returnedBasket.ident || basketIdent, returnedBasket);
          }

          let freshBasket = null;

          for (let attempt = 0; attempt < 4; attempt += 1) {
            if (attempt > 0) {
              await new Promise((resolve) => setTimeout(resolve, 450));
            }

            freshBasket = await refreshBasket(returnedBasket?.ident || basketIdent, true);

            if (freshBasket?.items?.length || previousItems.length === 0) {
              break;
            }
          }

          if (freshBasket?.items?.length || previousItems.length === 0) {
            saveBasket(freshBasket?.ident || returnedBasket?.ident || basketIdent, freshBasket || returnedBasket);
          } else if (previousBasket) {
            saveBasket(previousBasket.ident || basketIdent, {
              ...previousBasket,
              creator_code: type === "creator" ? null : previousBasket.creator_code,
              coupons: type === "coupon" ? [] : (previousBasket.coupons || []),
              total_price: returnedBasket?.total_price ?? previousBasket.total_price,
              base_price: returnedBasket?.base_price ?? previousBasket.base_price,
              sales_tax: returnedBasket?.sales_tax ?? previousBasket.sales_tax,
            });
          }

          toast.success(type === "coupon" ? "Coupon wurde entfernt." : "Creator Code wurde entfernt.");
        } else {
          toast.error(response.data.error || "Code konnte nicht entfernt werden.");
        }
      } catch (err) {
        toast.error(normalizeApiError(err));
      } finally {
        setBusy(false);
      }
    },
    [basket, basketIdent, refreshBasket, saveBasket],
  );

  const checkout = useCallback(async () => {
    if (!basketIdent) {
      toast.error("Dein Warenkorb ist leer.");
      return;
    }
    setBusy(true);
    try {
      const response = await axios.get(`${API}/tebex/basket/${encodeURIComponent(basketIdent)}/checkout`);
      if (response.data.success && response.data.data.checkout_url) {
        trackEvent("/analytics/checkout-click", { basket_ident: basketIdent });
        window.location.href = response.data.data.checkout_url;
      } else {
        toast.error(response.data.error || "Checkout-Link fehlt.");
      }
    } catch (err) {
      toast.error(normalizeApiError(err));
    } finally {
      setBusy(false);
    }
  }, [basketIdent]);

  useEffect(() => {
    if (basketIdent) refreshBasket(basketIdent, true);
  }, [basketIdent, refreshBasket]);

  return {
    basketIdent,
    basket,
    busy,
    cartOpen,
    setCartOpen,
    authPrompt,
    setAuthPrompt,
    saveBasket,
    refreshBasket,
    ensureBasket,
    addToCart,
    removeItem,
    updateQuantity,
    applyCode,
    removeCode,
    checkout,
    startAuthRedirect,
  };
}

function TopNav({ basket, onOpenCart }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const itemCount = basket?.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0;

  return (
    <header className="sticky top-0 z-40 border-b border-white/5 bg-[#09090b]/78 backdrop-blur-2xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
        <Link to="/" className="group flex items-center gap-3" data-testid="brand-home-link">
          <div className="grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-black/40 shadow-[0_0_28px_rgba(255,255,255,0.04)] transition-colors group-hover:bg-white/10 group-hover:border-white/20">
<div className="overflow-hidden rounded-full border border-white/10 bg-black/40 shadow-[0_0_28px_rgba(255,255,255,0.04)] transition-colors group-hover:bg-white/10 group-hover:border-white/20">
  <img
    src="/logo.gif"
    alt="Berlin Roleplay"
    className="h-11 w-11 object-cover"
  />
</div>

          </div>
          <div className="leading-tight">
            <div className="font-display text-2xl tracking-[0.14em] text-white uppercase">Berlin Roleplay</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/45">Official Tebex Store</div>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="Hauptnavigation">
          {navigation.map((item) => (
            <Link key={item.href} to={item.href} data-testid={item.testid} className="rounded-xl px-4 py-2 text-sm font-medium text-zinc-400 transition-colors hover:bg-white/5 hover:text-white">
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button variant="secondary" className="rounded-xl border border-white/10 bg-white/5 text-white hover:bg-white/10" onClick={onOpenCart} data-testid="cart-open-button">
            <ShoppingCart className="h-4 w-4" />
            <span className="hidden sm:inline">Warenkorb</span>
            <Badge className="ml-1 border-white/10 bg-white/10 text-zinc-100">{itemCount}</Badge>
          </Button>
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-open-button">
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </div>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="right" className="border-white/10 bg-[#09090b] text-white">
          <SheetHeader>
            <SheetTitle>Navigation</SheetTitle>
            <SheetDescription>Berlin Roleplay Shop Bereiche</SheetDescription>
          </SheetHeader>
          <div className="mt-8 grid gap-2">
            {navigation.map((item) => (
              <Link key={item.href} to={item.href} onClick={() => setMobileOpen(false)} className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white/80" data-testid={`${item.testid}-mobile`}>
                {item.label}
              </Link>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}

function Hero({ products, content = {} }) {
  const featuredCount = products.length;
  const heroImage = content.hero_image || "https://images.unsplash.com/flagged/photo-1635198193375-42c6b9c824ae?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";
  return (
    <section className="relative overflow-hidden border-b border-white/10">
      <div className="absolute inset-0 bg-[radial-gradient(closest-side_at_20%_10%,rgba(255,255,255,0.08),transparent_60%),radial-gradient(closest-side_at_80%_30%,rgba(255,255,255,0.05),transparent_55%)]" />
      <div className="absolute inset-0 bg-cover bg-center opacity-30 grayscale contrast-125" style={{ backgroundImage: `url('${heroImage}')` }} />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-[#09090b]/86 to-[#09090b]" />
      <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:py-28">
        <div className="max-w-3xl">
          <Badge className="mb-5 rounded-full border-white/10 bg-black/40 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 backdrop-blur-xl">Official Berlin Store</Badge>
          <h1 className="font-display text-5xl tracking-[0.13em] text-white sm:text-7xl lg:text-8xl uppercase" data-testid="home-hero-title">{content.hero_title || "Berlin Roleplay Shop"}</h1>
          <p className="mt-5 max-w-2xl text-base leading-8 text-white/72 sm:text-lg">{content.hero_description || "Willkommen im offiziellen Shop von Berlin Roleplay. Hier findest du ausgewählte Ressourcen und Angebote, die dein Spielerlebnis auf unserem Server erweitern und optimieren."}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className="rounded-full border border-white/20 bg-white/10 px-8 text-zinc-100 hover:bg-white/15 hover:text-white" data-testid="hero-shop-now-button">
              <Link to="/coins">{content.cta_primary || "Jetzt einkaufen"} <ChevronRight className="h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="secondary" className="rounded-2xl border border-white/10 bg-white/5 text-white hover:bg-white/10" data-testid="hero-discord-button">
              <a href={content.discord_link || "https://discord.gg/sWgGnT2TEb"} target="_blank" rel="noreferrer">{content.cta_secondary || "Discord Support"} <ExternalLink className="h-4 w-4" /></a>
            </Button>
          </div>
          <div className="mt-10 grid grid-cols-3 gap-3 sm:max-w-xl">
            <StatCard value={featuredCount} label="Produkte" />
            <StatCard value="FiveM" label="Auth" />
            <StatCard value="Tebex" label="Checkout" />
          </div>
        </div>
        <div className="flex items-center">
          <Card className="w-full border-white/5 bg-black/35 p-4 shadow-[0_28px_80px_rgba(0,0,0,0.45)] backdrop-blur-xl">
            <div className="rounded-2xl border border-white/5 bg-black/40 p-7 backdrop-blur-xl">
              <div className="mb-8 flex items-center justify-between">
                <div>
                  <div className="font-mono text-xs uppercase tracking-[0.24em] text-white/45">Berlin Roleplay</div>
                  <div className="mt-1 font-display text-4xl tracking-[0.12em] text-white uppercase">Premium Access</div>
                </div>
                <ShieldCheck className="h-10 w-10 text-zinc-300" />
              </div>
              <div className="space-y-3">
                {["Sichere Zahlung über Tebex", "Digitale Produkte direkt verfügbar", "Support über Discord Ticket", "FiveM Login vor dem Kauf"].map((text) => (
                  <div key={text} className="flex items-center gap-3 rounded-2xl border border-white/5 bg-white/[0.04] p-3 text-sm text-white/76">
                    <CheckCircle2 className="h-4 w-4 text-zinc-300" /> {text}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}

function StatCard({ value, label }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.055] p-4 backdrop-blur">
      <div className="font-display text-3xl tracking-[0.12em] text-white uppercase">{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">{label}</div>
    </div>
  );
}

function CategoryRail({ buckets }) {
  const counts = Object.fromEntries((buckets || []).map((bucket) => [bucket.id, bucket.products?.length || 0]));
  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14" data-testid="category-rail">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="section-eyebrow">Shop Kategorien</div>
          <h2 className="section-title">Wähle deinen Bereich</h2>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {Object.entries(bucketMeta).map(([key, meta]) => {
          const Icon = meta.icon;
          return (
            <Link key={key} to={`/${key}`} className="group rounded-2xl border border-white/5 bg-black/35 p-5 shadow-[0_10px_30px_rgba(0,0,0,0.25)] backdrop-blur-xl transition-colors hover:border-white/15 hover:bg-white/5" data-testid={`category-${key}-card`}>
              <div className="mb-5 flex items-center justify-between">
                <div className="grid h-12 w-12 place-items-center rounded-full border border-white/10 bg-white/5 text-zinc-400 group-hover:text-white"><Icon className="h-5 w-5" /></div>
                <Badge variant="outline" className="border-white/10 text-white/70">{counts[key] || 0} Produkte</Badge>
              </div>
              <div className="font-display text-3xl tracking-[0.12em] text-white uppercase">{meta.label}</div>
              <p className="mt-2 text-sm leading-6 text-white/58">{meta.description}</p>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function ProductGrid({ title, eyebrow, description, products, loading, error, onRetry, onAdd, onDetails }) {
  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14" data-testid="product-grid-section">
      <div className="mb-7 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <div className="section-eyebrow">{eyebrow}</div>
          <h2 className="section-title">{title}</h2>
          {description && <p className="mt-2 max-w-2xl text-sm leading-6 text-white/58">{description}</p>}
        </div>
        {onRetry && <Button variant="secondary" onClick={onRetry} data-testid="catalog-retry-button"><RefreshCw className="h-4 w-4" /> Aktualisieren</Button>}
      </div>
      {loading && <SkeletonGrid />}
      {!loading && error && <ErrorState message={error} onRetry={onRetry} />}
      {!loading && !error && products.length === 0 && <EmptyState />}
      {!loading && !error && products.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} onAdd={onAdd} onDetails={onDetails} />
          ))}
        </div>
      )}
    </section>
  );
}

function ProductCard({ product, onAdd, onDetails }) {
  return (
    <Card className="group flex min-h-[430px] flex-col overflow-hidden border-white/5 bg-black/35 shadow-[0_12px_36px_rgba(0,0,0,0.32)] backdrop-blur-xl transition-colors hover:border-white/15 hover:bg-white/[0.045]" data-testid={`product-card-${product.id}`}>
      <div className="relative h-48 overflow-hidden bg-[#09090b]">
        {product.image ? (
          <img src={product.image} alt={product.name} className="h-full w-full object-cover opacity-90 transition-transform duration-300 group-hover:scale-[1.035]" loading="lazy" />
        ) : (
          <div className="grid h-full place-items-center bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.06),transparent_55%)]">
            <Package className="h-12 w-12 text-white/32" />
          </div>
        )}
        <div className="absolute left-3 top-3"><Badge className="border-white/10 bg-white/10 text-zinc-100">{bucketMeta[product.bucket]?.label || "Shop"}</Badge></div>
      </div>
      <CardHeader className="pb-3">
        <CardTitle className="text-xl leading-7 text-white" data-testid={`product-name-${product.id}`}>{product.name}</CardTitle>
        <p className="line-clamp-2 min-h-[48px] text-sm leading-6 text-white/58">{product.short_description || "Digitales Produkt für Berlin Roleplay."}</p>
      </CardHeader>
      <CardContent className="mt-auto space-y-4">
        <div className="flex items-center justify-between">
          <span className="font-mono text-sm text-white/52">Preis</span>
          <span className="rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 font-mono text-sm text-zinc-100" data-testid="product-price">{formatMoney(product.price)}</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button className="rounded-full bg-white text-zinc-950 hover:bg-zinc-200" onClick={() => onAdd(product)} data-testid={`product-add-to-cart-button-${product.id}`}>
            <ShoppingCart className="h-4 w-4" /> Warenkorb
          </Button>
          <Button variant="secondary" className="rounded-xl border border-white/10 bg-white/5 text-white hover:bg-white/10" onClick={() => onDetails(product)} data-testid={`product-details-button-${product.id}`}>
            Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-loading-skeletons">
      {Array.from({ length: 6 }).map((_, index) => (
        <Card key={index} className="border-white/10 bg-[#111113]/70 p-4">
          <Skeleton className="h-44 rounded-2xl bg-white/8" />
          <Skeleton className="mt-5 h-6 w-2/3 bg-white/8" />
          <Skeleton className="mt-3 h-4 w-full bg-white/8" />
          <Skeleton className="mt-2 h-4 w-5/6 bg-white/8" />
          <div className="mt-6 grid grid-cols-2 gap-2"><Skeleton className="h-10 bg-white/8" /><Skeleton className="h-10 bg-white/8" /></div>
        </Card>
      ))}
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <Card className="border-red-400/20 bg-red-500/8" data-testid="error-banner">
      <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-3"><CircleAlert className="h-5 w-5 text-red-200" /><div><div className="font-semibold text-white">Katalog konnte nicht geladen werden</div><p className="mt-1 font-mono text-xs text-red-100/75">{message}</p></div></div>
        <Button onClick={onRetry} variant="secondary">Erneut versuchen</Button>
      </CardContent>
    </Card>
  );
}

function EmptyState() {
  return (
    <Card className="border-white/10 bg-[#111113]/70" data-testid="empty-products-state">
      <CardContent className="grid place-items-center p-10 text-center">
        <Package className="h-10 w-10 text-white/35" />
        <h3 className="mt-4 text-lg font-semibold text-white">Keine Produkte gefunden</h3>
        <p className="mt-2 max-w-md text-sm text-white/55">Aktuell sind in diesem Bereich keine Tebex-Produkte verfügbar.</p>
      </CardContent>
    </Card>
  );
}

function ProductDetailDialog({ product, open, onOpenChange, onAdd }) {
  const [quantity, setQuantity] = useState(1);
  useEffect(() => setQuantity(1), [product?.id]);
  if (!product) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto border-white/10 bg-[#09090b] text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-4xl tracking-wide">{product.name}</DialogTitle>
          <DialogDescription>{product.category_name || "Berlin Roleplay Shop"}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 md:grid-cols-[0.9fr_1.1fr]">
          <div className="overflow-hidden rounded-3xl border border-white/10 bg-[#09090b]">
            {product.image ? <img src={product.image} alt={product.name} className="h-full min-h-[280px] w-full object-cover" /> : <div className="grid min-h-[280px] place-items-center"><Package className="h-16 w-16 text-white/35" /></div>}
          </div>
          <div className="space-y-5">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/42">Beschreibung</div>
              <div className="prose prose-invert mt-3 max-w-none text-sm leading-7 text-white/72" dangerouslySetInnerHTML={{ __html: product.description || product.short_description || "<p>Digitales Produkt für Berlin Roleplay.</p>" }} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4"><div className="font-mono text-xs uppercase tracking-[0.2em] text-white/42">Preis</div><div className="mt-2 text-2xl font-semibold text-zinc-100">{formatMoney(product.price)}</div></div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4"><div className="font-mono text-xs uppercase tracking-[0.2em] text-white/42">Menge</div><div className="mt-2 flex items-center gap-2"><Button variant="secondary" size="icon" onClick={() => setQuantity(Math.max(1, quantity - 1))} data-testid="detail-quantity-minus-button"><Minus className="h-4 w-4" /></Button><span className="w-10 text-center font-mono">{quantity}</span><Button variant="secondary" size="icon" onClick={() => setQuantity(Math.min(50, quantity + 1))} data-testid="detail-quantity-plus-button"><Plus className="h-4 w-4" /></Button></div></div>
            </div>
            <div className="rounded-2xl border border-amber-300/20 bg-amber-300/8 p-4 text-sm leading-6 text-amber-100/82"><ShieldCheck className="mr-2 inline h-4 w-4" />Digitale Käufe sind endgültig. Mit dem Kauf verzichtest du automatisch auf dein Widerrufsrecht.</div>
            <Button className="w-full rounded-2xl bg-white text-zinc-950 hover:bg-zinc-200" onClick={() => onAdd(product, quantity)} data-testid="detail-add-to-cart-button"><ShoppingCart className="h-4 w-4" /> In den Warenkorb</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CodeTools({ basketApi, compact = false }) {
  const [giftCode, setGiftCode] = useState("");
  const [giftResult, setGiftResult] = useState(null);
  const [creatorCode, setCreatorCode] = useState("");
  const [checkingGift, setCheckingGift] = useState(false);

  const checkGift = async () => {
    if (!giftCode.trim()) return;
    setCheckingGift(true);
    setGiftResult(null);
    try {
      const response = await axios.post(`${API}/tebex/giftcard/check`, { code: giftCode.trim() });
      if (response.data.success) {
        setGiftResult({ ok: true, text: formatGiftcardResult(response.data.data) });
      } else {
        setGiftResult({ ok: false, text: response.data.error || "Gutschein nicht gültig." });
      }
    } catch (err) {
      setGiftResult({ ok: false, text: normalizeApiError(err) });
    } finally {
      setCheckingGift(false);
    }
  };

  const applyCreator = async () => {
    const ok = await basketApi.applyCode("creator", creatorCode);
    if (ok) setCreatorCode("");
  };

  return (
    <div className={`grid gap-4 ${compact ? "" : "lg:grid-cols-2"}`}>
      <Card className="border-white/10 bg-[#111113]/78" data-testid="giftcard-check-card">
        <CardHeader><CardTitle className="flex items-center gap-2 text-white"><Gift className="h-5 w-5 text-zinc-100" /> Gutschein prüfen</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Input value={giftCode} onChange={(e) => setGiftCode(e.target.value)} placeholder="Gutschein-/Giftcard-Code" className="border-white/10 bg-[#09090b] font-mono text-white" data-testid="giftcard-check-input" />
          <Button onClick={checkGift} disabled={checkingGift} className="w-full rounded-full bg-white text-zinc-950 hover:bg-zinc-200" data-testid="giftcard-check-submit-button">{checkingGift ? "Prüfe..." : "Wert prüfen"}</Button>
          {giftResult && <div className={`rounded-xl border p-3 text-sm ${giftResult.ok ? "border-emerald-300/20 bg-emerald-300/8 text-emerald-100" : "border-red-300/20 bg-red-300/8 text-red-100"}`} data-testid="giftcard-check-result">{giftResult.text}</div>}
        </CardContent>
      </Card>
      <Card className="border-white/10 bg-[#111113]/78" data-testid="creator-code-card">
        <CardHeader><CardTitle className="flex items-center gap-2 text-white"><Ticket className="h-5 w-5 text-zinc-100" /> Creator Code eintragen</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Input value={creatorCode} onChange={(e) => setCreatorCode(e.target.value)} placeholder="Creator Code" className="border-white/10 bg-[#09090b] font-mono text-white" data-testid="creator-code-input" />
          <Button onClick={applyCreator} disabled={basketApi.busy} className="w-full rounded-xl bg-white text-zinc-950 hover:bg-white/88" data-testid="creator-code-apply-button">Creator Code anwenden</Button>
          <p className="text-xs leading-5 text-white/45">Der Code wird auf deinen aktuellen Tebex-Warenkorb angewendet. Falls ein Rabatt entsteht, aktualisiert Tebex die Summe automatisch.</p>
        </CardContent>
      </Card>
    </div>
  );
}

function LegalAndSupport({ content = {} }) {
  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
      <div className="mb-7"><div className="section-eyebrow">Informationen</div><h2 className="section-title">Support & Richtlinien</h2></div>
      <div className="grid gap-4 lg:grid-cols-[0.8fr_1.2fr]">
        <Card className="border-white/10 bg-white/[0.04]">
          <CardHeader><CardTitle className="text-white">Discord Support</CardTitle></CardHeader>
          <CardContent className="space-y-4 text-sm leading-7 text-white/68">
            <p>{content.support_text || "Bei Fragen oder Problemen steht dir unser Support jederzeit zur Verfügung. Bitte öffne hierfür ein Ticket auf unserem Discord-Server."}</p>
            <Button asChild className="rounded-full bg-white text-zinc-950 hover:bg-zinc-200" data-testid="discord-support-link"><a href={content.discord_link || "https://discord.gg/sWgGnT2TEb"} target="_blank" rel="noreferrer">Discord öffnen <ExternalLink className="h-4 w-4" /></a></Button>
          </CardContent>
        </Card>
        <Card className="border-white/10 bg-[#111113]/78">
          <CardContent className="p-2 sm:p-4">
            <Accordion type="single" collapsible defaultValue="intro" className="w-full" data-testid="legal-accordion">
              <AccordionItem value="intro" className="border-white/10"><AccordionTrigger>Berlin Roleplay Shop</AccordionTrigger><AccordionContent><p>{content.hero_description || "Willkommen im offiziellen Shop von Berlin Roleplay. Hier findest du ausgewählte Ressourcen und Angebote, die dein Spielerlebnis auf unserem Server erweitern und optimieren. Unser Ziel ist es, dir hochwertige Inhalte bereitzustellen und ein professionelles Spielerlebnis zu gewährleisten."}</p></AccordionContent></AccordionItem>
              <AccordionItem value="refund" className="border-white/10"><AccordionTrigger>Rückerstattung und Widerruf</AccordionTrigger><AccordionContent><p>{content.legal_refund || "Ein Umtausch ist nicht möglich. Mit dem Kauf verzichtest du automatisch auf dein Widerrufsrecht."}</p></AccordionContent></AccordionItem>
              <AccordionItem value="returns" className="border-white/10"><AccordionTrigger>Rückgaberichtlinie</AccordionTrigger><AccordionContent><div className="space-y-3"><p>{content.legal_returns || "Alle Zahlungen sind endgültig und nicht erstattungsfähig. Der Versuch einer Rückbuchung oder das Einreichen eines PayPal-Disputs führt zu einem permanenten und unwiderruflichen Ausschluss von all unseren Servern sowie allen damit verbundenen FiveM-Shops."}</p><p>Erworbene Produkte können in der Regel unmittelbar nach dem Kauf im Spiel eingelöst werden. Detaillierte Informationen zu den einzelnen Artikeln findest du in deren jeweiligen Beschreibungen.</p><p>Solltest du ein Problem haben, öffne bitte ein Support-Ticket auf unserem Discord-Server und füge einen gültigen Kaufnachweis hinzu, damit wir dein Anliegen ordnungsgemäß bearbeiten können.</p></div></AccordionContent></AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function CartSheet({ basketApi }) {
  const basket = basketApi.basket;
  const [coupon, setCoupon] = useState("");
  const [couponResult, setCouponResult] = useState(null);
  const [creator, setCreator] = useState("");
  const items = basket?.items || [];

  const applyCoupon = async () => {
    if (!coupon.trim()) return;

    setCouponResult(null);

    const ok = await basketApi.applyCode("coupon", coupon);

    if (ok) {
      setCoupon("");
      setCouponResult({ ok: true, text: "Coupon wurde angewendet." });
      return;
    }

    try {
      const response = await axios.post(`${API}/tebex/giftcard/check`, { code: coupon.trim() });

      if (response.data.success) {
        setCouponResult({
          ok: true,
          text: `${formatGiftcardResult(response.data.data)} · Giftcards werden beim Tebex Checkout im Feld Coupon/Gift Card eingelöst.`,
        });
        toast.success("Giftcard wurde gefunden. Du kannst sie im Tebex Checkout einlösen.");
      } else {
        setCouponResult({ ok: false, text: response.data.error || "Coupon oder Giftcard nicht gültig." });
      }
    } catch (err) {
      setCouponResult({ ok: false, text: normalizeApiError(err) });
    }
  };
  const applyCreator = async () => {
    const ok = await basketApi.applyCode("creator", creator);
    if (ok) setCreator("");
  };

  return (
    <Sheet open={basketApi.cartOpen} onOpenChange={basketApi.setCartOpen}>
      <SheetContent className="flex w-full flex-col border-white/10 bg-[#09090b] text-white sm:max-w-xl">
        <SheetHeader>
          <SheetTitle className="font-display text-3xl tracking-[0.12em] uppercase">Warenkorb</SheetTitle>
          <SheetDescription>{basket?.is_authenticated ? `Verbunden als ${basket.username}` : "FiveM Login ist spätestens beim Hinzufügen erforderlich."}</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto py-5">
          {items.length === 0 ? (
            <div className="grid min-h-[260px] place-items-center rounded-3xl border border-white/10 bg-white/[0.035] p-8 text-center" data-testid="cart-empty-state"><ShoppingCart className="h-10 w-10 text-white/35" /><div><div className="mt-4 font-semibold">Dein Warenkorb ist leer</div><p className="mt-2 text-sm text-white/52">Entdecke Coins, Imports oder Pakete.</p></div></div>
          ) : (
            <div className="space-y-3" data-testid="cart-items-list">
              {items.map((item) => (
                <div key={item.id} className="rounded-2xl border border-white/10 bg-white/[0.045] p-4" data-testid={`cart-item-${item.id}`}>
                  <div className="flex gap-3">
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-[#09090b]">{item.image ? <img src={item.image} alt={item.name} className="h-full w-full object-cover" /> : <Package className="m-5 h-6 w-6 text-white/35" />}</div>
                    <div className="min-w-0 flex-1"><div className="truncate font-semibold text-white">{item.name}</div><div className="mt-1 font-mono text-xs text-zinc-100">{formatMoney(item.line_price || item.price, basket.currency)}</div><div className="mt-3 flex items-center gap-2"><Button size="icon" variant="secondary" onClick={() => basketApi.updateQuantity(item.id, Math.max(0, item.quantity - 1))} data-testid={`cart-item-decrease-${item.id}`}><Minus className="h-4 w-4" /></Button><span className="w-8 text-center font-mono text-sm">{item.quantity}</span><Button size="icon" variant="secondary" onClick={() => basketApi.updateQuantity(item.id, item.quantity + 1)} data-testid={`cart-item-increase-${item.id}`}><Plus className="h-4 w-4" /></Button><Button size="icon" variant="ghost" onClick={() => basketApi.removeItem(item.id)} data-testid={`cart-item-remove-${item.id}`}><Trash2 className="h-4 w-4 text-red-200" /></Button></div></div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-5 grid gap-3">
            <div className="rounded-2xl border border-white/10 bg-white/[0.035] p-4"><div className="mb-2 text-sm font-semibold">Coupon / Giftcard</div><div className="flex gap-2"><Input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon- oder Giftcard-Code" className="border-white/10 bg-[#09090b] font-mono" data-testid="cart-coupon-input" /><Button onClick={applyCoupon} disabled={basketApi.busy} data-testid="cart-coupon-apply-button">Anwenden</Button></div>{couponResult && <div className={`mt-2 rounded-xl border p-3 text-xs leading-5 ${couponResult.ok ? "border-emerald-300/20 bg-emerald-300/8 text-emerald-100" : "border-red-300/20 bg-red-300/8 text-red-100"}`} data-testid="cart-coupon-result">{couponResult.text}</div>}{basket?.coupons?.length > 0 && <Button variant="ghost" className="mt-2" onClick={() => basketApi.removeCode("coupon")} data-testid="cart-coupon-remove-button"><X className="h-4 w-4" /> Coupon entfernen</Button>}</div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.035] p-4"><div className="mb-2 text-sm font-semibold">Creator Code</div><div className="flex gap-2"><Input value={creator} onChange={(e) => setCreator(e.target.value)} placeholder="Creator Code" className="border-white/10 bg-[#09090b] font-mono" data-testid="cart-creator-code-input" /><Button onClick={applyCreator} disabled={basketApi.busy} data-testid="cart-creator-code-apply-button">Anwenden</Button></div>{basket?.creator_code && <Button variant="ghost" className="mt-2" onClick={() => basketApi.removeCode("creator")} data-testid="cart-creator-code-remove-button"><X className="h-4 w-4" /> Creator Code entfernen</Button>}</div>
          </div>
        </div>
        <div className="border-t border-white/10 pt-4">
          <div className="mb-4 flex items-center justify-between"><span className="text-sm text-white/58">Zwischensumme</span><span className="font-display text-3xl tracking-[0.12em] text-white uppercase" data-testid="cart-total-price">{formatMoney(basket?.total_price || 0, basket?.currency || "EUR")}</span></div>
          <Button onClick={basketApi.checkout} disabled={basketApi.busy || items.length === 0} className="w-full rounded-2xl bg-white text-zinc-950 hover:bg-zinc-200" data-testid="cart-checkout-button">Zum Tebex Checkout <ExternalLink className="h-4 w-4" /></Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function AuthRequiredDialog({ prompt, onClose, onStart }) {
  return (
    <Dialog open={!!prompt} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="border-white/10 bg-[#09090b] text-white" data-testid="auth-required-banner">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display text-3xl tracking-[0.12em] uppercase"><LogIn className="h-6 w-6 text-zinc-100" /> Anmeldung erforderlich</DialogTitle><DialogDescription>Bevor du {prompt?.productName || "dieses Produkt"} in den Warenkorb legen kannst, musst du dich mit deinem FiveM/Tebex Account verbinden.</DialogDescription></DialogHeader>
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-sm leading-6 text-white/68">Tebex verlangt diese Anmeldung für FiveM-Stores, damit dein Kauf korrekt deinem Account zugeordnet wird. Danach kehrst du automatisch zum Shop zurück.</div>
        <Button className="rounded-2xl bg-white text-zinc-950 hover:bg-zinc-200" onClick={onStart} data-testid="auth-required-connect-button">Mit FiveM verbinden <ExternalLink className="h-4 w-4" /></Button>
      </DialogContent>
    </Dialog>
  );
}

function HomePage({ shop, basketApi, onAdd, onDetails, content }) {
  const featured = shop.products.slice(0, 6);
  return (
    <>
      <Hero products={shop.products} content={content} />
      <CategoryRail buckets={shop.buckets} />
      <ProductGrid title="Beliebte Produkte" eyebrow="Featured" description="Ausgewählte Angebote direkt aus deinem echten Tebex-Katalog." products={featured} loading={shop.loading} error={shop.error} onRetry={shop.reload} onAdd={onAdd} onDetails={onDetails} />
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14"><div className="mb-7"><div className="section-eyebrow">Codes</div><h2 className="section-title">Gutschein prüfen & Creator Code</h2></div><CodeTools basketApi={basketApi} /></section>
      <LegalAndSupport content={content} />
    </>
  );
}

function CategoryPage({ bucket, shop, onAdd, onDetails }) {
  const meta = bucketMeta[bucket] || bucketMeta.pakete;
  const products = shop.products.filter((product) => product.bucket === bucket);
  return <ProductGrid title={meta.label} eyebrow={meta.eyebrow} description={meta.description} products={products} loading={shop.loading} error={shop.error} onRetry={shop.reload} onAdd={onAdd} onDetails={onDetails} />;
}

function CartPage({ basketApi }) {
  return (
    <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
      <div className="mb-8"><div className="section-eyebrow">Warenkorb</div><h1 className="section-title">Dein Einkauf</h1></div>
      <CodeTools basketApi={basketApi} compact />
      <div className="mt-6"><Button onClick={() => basketApi.setCartOpen(true)} data-testid="cart-page-open-sheet-button">Warenkorb öffnen</Button></div>
    </section>
  );
}

function AuthReturnPage({ shop, basketApi }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [message, setMessage] = useState("FiveM Anmeldung wird verarbeitet...");

  useEffect(() => {
    const run = async () => {
      const params = new URLSearchParams(location.search);
      const basket = params.get("basket") || localStorage.getItem(BASKET_KEY);
      if (basket) basketApi.saveBasket(basket);
      const pendingRaw = localStorage.getItem(PENDING_ADD_KEY);
      const pending = pendingRaw ? JSON.parse(pendingRaw) : null;
      await basketApi.refreshBasket(basket, true);
      if (pending?.productId) {
        const product = shop.products.find((item) => item.id === pending.productId) || { id: pending.productId, name: pending.productName || "Produkt" };
        setMessage("Produkt wird nach der Anmeldung hinzugefügt...");
        localStorage.removeItem(PENDING_ADD_KEY);
        await basketApi.addToCart(product, pending.quantity || Number(params.get("qty")) || 1);
      }
      setMessage("Anmeldung abgeschlossen. Du wirst zum Warenkorb weitergeleitet.");
      basketApi.setCartOpen(true);
      setTimeout(() => navigate("/warenkorb"), 900);
    };
    if (!shop.loading) run();
    // This return handler intentionally runs once after catalog loading for the current redirect URL.
    // eslint-disable-next-line
  }, [location.search, navigate, shop.loading]);

  return <section className="mx-auto grid min-h-[60vh] max-w-3xl place-items-center px-4"><Card className="border-white/10 bg-[#111113]/82 p-8 text-center"><LogIn className="mx-auto h-10 w-10 text-zinc-100" /><h1 className="mt-5 font-display text-4xl tracking-[0.12em] text-white uppercase" data-testid="auth-return-title">FiveM verbunden</h1><p className="mt-3 text-white/62" data-testid="auth-return-message">{message}</p></Card></section>;
}

function SuccessPage() {
  return <section className="mx-auto grid min-h-[60vh] max-w-3xl place-items-center px-4"><Card className="border-emerald-300/20 bg-emerald-300/8 p-8 text-center"><CheckCircle2 className="mx-auto h-12 w-12 text-emerald-200" /><h1 className="mt-5 font-display text-4xl tracking-[0.12em] text-white uppercase" data-testid="checkout-success-title">Danke für deinen Einkauf</h1><p className="mt-3 text-white/62">Solltest du Fragen haben, öffne bitte ein Discord-Ticket mit deinem Kaufnachweis.</p><Button asChild className="mt-6"><Link to="/">Zur Startseite</Link></Button></Card></section>;
}


function ImpressumPage() {
  return (
    <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
      <div className="mb-10">
        <div className="section-eyebrow">Rechtliches</div>
        <h1 className="section-title">Impressum</h1>
      </div>

      <Card className="border-white/10 bg-[#111113]/80">
        <CardContent className="space-y-6 p-8 text-sm leading-7 text-white/70">

          <div>
            <h2 className="mb-2 text-lg font-semibold text-white">
              Angaben gemäß § 5 TMG
            </h2>

            <p>
              Berlin Roleplay Tebex Store
              <br />
              Digitale Produkte für FiveM / GTA Roleplay
            </p>
          </div>

          <div>
            <h2 className="mb-2 text-lg font-semibold text-white">
              Zahlungsabwicklung
            </h2>

            <p>
              Die Zahlungsabwicklung erfolgt über den externen Anbieter Tebex Ltd.
              <br />
              Tebex ist verantwortlich für Checkout, Zahlungsabwicklung,
              Rechnungen und steuerliche Verarbeitung.
            </p>
          </div>

          <div>
            <h2 className="mb-2 text-lg font-semibold text-white">
              Anbieter
            </h2>

            <p>
              Tebex Ltd
              <br />
              https://www.tebex.io
            </p>
          </div>

          <div>
            <h2 className="mb-2 text-lg font-semibold text-white">
              Support
            </h2>

            <p>
              Support-Anfragen bitte ausschließlich über unser Discord-Ticket-System.
            </p>

            <a
              href="https://discord.gg/sWgGnT2TEb"
              target="_blank"
              rel="noreferrer"
              className="text-white underline"
            >
              Discord Server öffnen
            </a>
          </div>

          <div>
            <h2 className="mb-2 text-lg font-semibold text-white">
              Hinweis
            </h2>

            <p>
              Alle genannten Marken, Spiele und Inhalte gehören den jeweiligen Eigentümern.
              Dies ist ein unabhängiger Community-Shop für Berlin Roleplay.
            </p>
          </div>

        </CardContent>
      </Card>
    </section>
  );
}

function NotFoundPage() {
  return <section className="mx-auto grid min-h-[60vh] max-w-3xl place-items-center px-4"><Card className="border-white/10 bg-[#111113]/82 p-8 text-center"><h1 className="font-display text-6xl tracking-wide text-white" data-testid="not-found-title">404</h1><p className="mt-3 text-white/62">Diese Seite wurde nicht gefunden.</p><Button asChild className="mt-6"><Link to="/">Zur Startseite</Link></Button></Card></section>;
}

function MobileStickyCart({ basket, onOpen }) {
  const itemCount = basket?.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0;
  if (!itemCount) return null;
  return <div className="fixed inset-x-3 bottom-3 z-30 md:hidden"><Button onClick={onOpen} className="w-full rounded-2xl bg-white py-6 text-zinc-950 shadow-[0_20px_60px_rgba(0,0,0,0.45)]" data-testid="mobile-sticky-cart-button"><ShoppingCart className="h-4 w-4" /> Warenkorb ({itemCount}) · {formatMoney(basket?.total_price || 0, basket?.currency || "EUR")}</Button></div>;
}

function AppShell() {
  const shop = useShop();
  const basketApi = useBasket();
  const location = useLocation();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [siteContent, setSiteContent] = useState({});
  const [blacklistStatus, setBlacklistStatus] = useState(null);

  useEffect(() => {
    axios.get(`${API}/site-content`).then((res) => setSiteContent(res.data.data || {})).catch(() => {});
    axios.get(`${API}/blacklist/status`).then((res) => setBlacklistStatus(res.data.data || null)).catch(() => {});
  }, []);

  useEffect(() => {
    trackEvent("/analytics/page-view", { path: location.pathname, referrer: document.referrer });
  }, [location.pathname]);

  const handleAdd = async (product, quantity = 1) => {
    trackEvent("/analytics/cart-start", { product_id: product.id, basket_ident: basketApi.basketIdent });
    await basketApi.addToCart(product, quantity);
  };

  const handleDetails = (product) => {
    trackEvent("/analytics/product-click", { product_id: product.id, product_name: product.name, category: product.category_name, page: location.pathname, referrer: document.referrer });
    setSelectedProduct(product);
  };

  const startPromptAuth = () => {
    if (!basketApi.authPrompt) return;
    basketApi.startAuthRedirect(basketApi.authPrompt.authUrl, basketApi.authPrompt.pendingAdd);
  };

  const shouldShowBlacklist = blacklistStatus?.blacklisted && !location.pathname.startsWith("/admin") && !location.pathname.startsWith("/creator") && !location.pathname.startsWith("/auth");

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100">
      <TopNav basket={basketApi.basket} onOpenCart={() => basketApi.setCartOpen(true)} />
      <main>
        {shouldShowBlacklist ? (
          <BlacklistedPage status={blacklistStatus} />
        ) : (
          <Routes>
            <Route path="/" element={<HomePage shop={shop} basketApi={basketApi} onAdd={handleAdd} onDetails={handleDetails} content={siteContent} />} />
            <Route path="/coins" element={<CategoryPage bucket="coins" shop={shop} onAdd={handleAdd} onDetails={handleDetails} />} />
            <Route path="/imports" element={<CategoryPage bucket="imports" shop={shop} onAdd={handleAdd} onDetails={handleDetails} />} />
            <Route path="/pakete" element={<CategoryPage bucket="pakete" shop={shop} onAdd={handleAdd} onDetails={handleDetails} />} />
            <Route path="/warenkorb" element={<CartPage basketApi={basketApi} />} />
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/creator" element={<CreatorDashboardPage />} />
            <Route path="/auth/fivem-return" element={<FiveMReturnPage />} />
            <Route path="/auth/tebex-return" element={<AuthReturnPage shop={shop} basketApi={basketApi} />} />
            <Route path="/checkout/success" element={<SuccessPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />


            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        )}
      </main>

<footer className="border-t border-white/5 px-4 py-8 text-center text-xs text-zinc-600">
  <div className="space-y-2">
    <div>
      {siteContent.footer_text || "Berlin Roleplay Shop · Sichere Abwicklung über Tebex"} · 
      <Link to="/admin" className="hover:text-zinc-300 ml-1">
        Admin
      </Link> · 
      <Link to="/creator" className="hover:text-zinc-300 ml-1">
        Creator
      </Link> · 
      <a
        href="https://checkout.tebex.io/impressum"
        target="_blank"
        rel="noreferrer"
        className="hover:text-zinc-300 ml-1"
      >
        Impressum
      </a>
    </div>

    <div className="text-[11px] text-zinc-700">
      Zahlungen und Checkout werden sicher über Tebex abgewickelt.
    </div>
  </div>
</footer>


      <ProductDetailDialog product={selectedProduct} open={!!selectedProduct} onOpenChange={(open) => !open && setSelectedProduct(null)} onAdd={handleAdd} />
      <CartSheet basketApi={basketApi} />
      <AuthRequiredDialog prompt={basketApi.authPrompt} onClose={() => basketApi.setAuthPrompt(null)} onStart={startPromptAuth} />
      <MobileStickyCart basket={basketApi.basket} onOpen={() => basketApi.setCartOpen(true)} />
      <Toaster richColors position="top-right" theme="dark" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;
